import { CompactCard } from '@jo-v1/components';
import Dots from '@jo-v1/components/Loading/Dots';
import TcpaSection from '@jo-v1/features/TcpaSection';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { v4 } from 'uuid';

import { LEAD_SUBMISSION_STEPS } from '@core/constants';
import useFormEvents from '@core/hooks/cohesion/useFormEvents';
import { selectFormContext } from '@core/reducers/eventingSlice';
import JoPostResultForm from '@core/shared/features/PostResultForm';
import { type VoyagerResult } from '@core/ts/results';

import styles from './JoAdditionalQuestions.module.css';

type JoAdditionalQuestionsProps = {
  setPiiLeadErrors: Dispatch<SetStateAction<{ field: string }[]>>;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  setStep: Dispatch<SetStateAction<string>>;
  currentProgram: VoyagerResult;
  prqSchema: any;
  formErrored: () => void;
  originalSchema: any;
  isResultsRevamp?: boolean;
  tcpa: string;
  tcpaLoading: boolean;
  tcpaErrorMsg?: string;
};

const { EXIT_PAGE, EXIT_STRATEGY } = LEAD_SUBMISSION_STEPS;
const JoAdditionalQuestions = ({
  currentProgram,
  originalSchema,
  prqSchema,
  setStep,
  isResultsRevamp = false,
  setPiiLeadErrors,
  setEditMode,
  formErrored,
  tcpa,
  tcpaLoading,
  tcpaErrorMsg,
}: JoAdditionalQuestionsProps) => {
  const formCorrelationId = useMemo(() => v4(), []);
  const formContext = useSelector(selectFormContext);

  const { formViewed, formStarted, formSubmitted } = useFormEvents({
    correlationId: formCorrelationId,
    formContext: {
      ...formContext,
      formVersion: 'dynamic-post-flow',
      formType: 'post-flow',
      formBrand: currentProgram?.school?.slug,
      formId: '1015',
    },
    stepContext: {},
  });

  const onMount = () => {
    formViewed();
  };

  const onFirstInteraction = () => {
    formStarted();
  };

  const onSubmit = () => {
    setStep(isResultsRevamp ? EXIT_PAGE : EXIT_STRATEGY);
    formSubmitted();
  };

  if (tcpaLoading)
    return (
      <section className={styles.loadingContainer}>
        {tcpaErrorMsg ? (
          <h2>{tcpaErrorMsg}</h2>
        ) : (
          <>
            <h2>Loading...</h2>
            <Dots />
          </>
        )}
      </section>
    );

  return (
    <section className={styles.postResultForm}>
      <div>
        <h4>Additional info required.</h4>
        <p>
          The program you’ve selected requires the following information to process your inquiry. To ensure the school
          receives it, please answer all of the fields below. Thank you.
        </p>

        <CompactCard hit={currentProgram} showClose={false} />
      </div>

      <div className={styles.form}>
        <JoPostResultForm
          formCorrelationId={formCorrelationId}
          originalSchema={originalSchema}
          hit={currentProgram}
          prqSchema={prqSchema}
          setPiiLeadErrors={setPiiLeadErrors}
          setEditMode={setEditMode}
          setStep={setStep}
          onFormError={formErrored}
          onMount={onMount}
          onFirstInteraction={onFirstInteraction}
          onSubmit={onSubmit}
        >
          <div className={styles.spacer} />
          <TcpaSection title="" tcpa={tcpa ? [tcpa] : null} />
          <div className={styles.cta}>
            <b>Double check your information before submitting</b>
            <p>Your information will never be shared with a school without your consent.</p>
            <button type="submit" className={styles.submitBtn}>
              Submit
            </button>
          </div>
        </JoPostResultForm>
      </div>
    </section>
  );
};

export default JoAdditionalQuestions;
